import { createSelector } from "reselect"
import { get, toLower, chain, map } from "lodash"
import { computePositionSet } from "../helpers/security"
import { getGroup, menuContentSets } from "helpers/menu"
import { isCompositeSecurity } from "helpers/compositeSecurities"
import { unsupportedCurrencyWarning } from "helpers/currency"
import { getIsOpPro, getDefaultCurrencyId } from "modules/account-root/selectors"
import { getMissingDataFiles, betasAreMissing, modelSupportsComposites } from "selectors/missingDataFiles"
import { getContentSets } from "selectors/contentSets"
import { getModelSupportedCurrencies } from "selectors/modelMetadata"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []
export const getRawSecurity = state => get(state, "global.data.securityDescriptors.data.model.security", EMPTY_OBJECT)
export const getWatchlistsMenuVisible = state => get(state, "modules.securityProfile.watchlistsMenuVisible", false)
export const getSecurityWatchlists = state => get(state, "global.data.securityWatchlists.data", EMPTY_ARRAY)
export const getForecasts = state => get(state, "global.data.forecasts.data.forecasts", EMPTY_ARRAY)
const getETFDescriptor = state => get(state, "global.data.etfSecurityDescriptors.data.0")
const getClassifications = state => get(state, "global.data.classifications.data.classifications", EMPTY_ARRAY)
const getBaseRaw = state => get(state, "modules.securityProfile.base")
const getBaseNameRaw = state => get(state, "modules.securityProfile.baseName")
const getBaseSecurityDescriptors = state => get(state, "global.data.baseSecurityDescriptors.data.model.security.descriptors")
const getModelId = (state, props) => get(props, "modelId")

const getClassificationMap = createSelector(
  [getClassifications],
  (classifications) => chain(classifications)
    .keyBy("id")
    .mapValues("name")
    .value(),
)

export const getSecurity = createSelector(
  [getRawSecurity, getClassificationMap],
  (security, classifications) => ({
    ...get(security, "descriptors"),
    gics: get(security, "GICS.name"),
    otherClassifications: chain(security)
      .omit(["descriptors", "GICS"])
      .map((value, key) => value && { name: get(classifications, key), value: get(value, "name") })
      .compact()
      .value(),
  }),
)

export const getRawSecurityId = (state, props) => chain(props)
  .get("security")
  .mapKeys((value, key) => key === "exchange" ? "mic" : key)
  .value()

export const getBaseType = createSelector(
  [getBaseRaw],
  (base) => {
    if (!base) {
      return null
    }

    return toLower(base.type)
  },
)

export const getBaseId = createSelector(
  [getBaseRaw],
  (base) => {
    if (!base) {
      return null
    }

    return base.id
  },
)

export const getBase = createSelector(
  [getBaseRaw, getSecurity, getBaseSecurityDescriptors],
  (base, security, baseDescriptors) => {
    if (!base) {
      return null
    }

    switch (base.type) {
      case "security": {
        if (base.ticker === security.ticker && base.mic === security.mic) {
          return null // Don't run compare on same security
        }
        return computePositionSet(base.assetClass, base.ticker, base.mic, get(baseDescriptors, "modelProviderId"))
      }
      case "BENCHMARK":
      case "INDEX":
      case "PORTFOLIO": {
        return base
      }
    }
  },
)

export const getBaseName = createSelector(
  [getBaseRaw, getBaseNameRaw, getSecurity, getBaseSecurityDescriptors],
  (base, baseName, security, baseSecurityDescriptors) => {
    if (base && base.type === "security") {
      if (base.ticker === security.ticker && base.mic === security.mic) {
        return null // Don't run compare on same security
      } else if (baseSecurityDescriptors?.name) {
        return `${baseSecurityDescriptors.ticker} - ${baseSecurityDescriptors.name}`
      } else {
        return base.ticker
      }
    }

    return baseName
  },
)

const getDisplayTypes = createSelector(
  [getContentSets, getMissingDataFiles, getModelId, getForecasts, getIsOpPro],
  (contentSets, missingDataFiles, modelId, forecasts, isOpPro) => {
    const missingBetas = betasAreMissing(missingDataFiles, modelId)
    return [{
      id: "performance", name: "Performance", base: true, tooltip: "Ex-post performance with factor attribution",
    }, {
      id: "risk", name: "Predictive Risk", base: true, disabled: isOpPro, tooltip: isOpPro ? "Only available with enterprise plan" : "Ex-ante total risk & factor risk decomposition",
    }, {
      id: "exposure", name: "Exposure", base: true, tooltip: "Weighted-average factor exposure",
    },
    {
      id: "composition",
      name: "Composition",
      base: true,
      disabled: missingBetas,
      tooltip: missingBetas ? "There are no Predicted or Historical beta values for the selected model" : "Beta as provided by the selected risk model",
    }, {
      type: "factorSensitivity",
      id: "predicted",
      name: "Predicted Factor Sensitivity",
      base: true,
      disabled: isOpPro,
      tooltip: isOpPro
        ? "Only available with enterprise plan"
        : "Expected return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using the risk model covariance matrix.",
    }, {
      type: "factorSensitivity",
      id: "historical",
      name: "Historical Factor Sensitivity",
      base: true,
      tooltip: "Expected return impact from factors based on factor exposure and expected factor volatility. Factor volatility is estimated using historical realized factor returns.",
    },
    ...map(forecasts, (forecast) => ({ ...forecast, type: "forecast" })),
    ...menuContentSets(contentSets),
    ]
  },
)

export const getMenuItems = createSelector(
  [getDisplayTypes],
  (types) => map(types, (type, i) => ({
    order: i,
    id: type.id,
    label: type.name,
    ...getGroup(type),
    ...type,
  })),
)

export const getAnalyzeCompositeAvailable = createSelector(
  [getMissingDataFiles, getSecurity, getETFDescriptor],
  (missingDataFiles, security, etfDescriptor) => {
    if (security.assetSubclass === "etf") {
      return modelSupportsComposites(missingDataFiles) && get(etfDescriptor, "hasComposition")
    }

    return get(security, "assetClass") === "swap"
  },
)

export const getDisplayCompositeLink = createSelector(
  [getSecurity],
  isCompositeSecurity,
)

const getBasketCurrencyOverride = state => get(state, "global.data.basketResource.data.resource.currencyOverride")

const getCurrencyOverride = createSelector(
  [getDefaultCurrencyId, getBasketCurrencyOverride],
  (accountCurrencyId, basketCurrencyOverride) => basketCurrencyOverride || accountCurrencyId,
)

export const getUnsupportedCurrencyWarning = createSelector(
  [getModelSupportedCurrencies, getDefaultCurrencyId, getBasketCurrencyOverride],
  unsupportedCurrencyWarning,
)

export const getCurrency = createSelector(
  [getCurrencyOverride, getUnsupportedCurrencyWarning],
  (currencyOverride, unsupportedCurrencyWarning) => unsupportedCurrencyWarning ? null : currencyOverride,
)
